import { useEffect, useState } from 'react';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import { useStyles } from "../../common/appStyles";
import { FormControl, Paper, ThemeProvider, createTheme } from '@mui/material';
import { showSnackbar } from '../../services/snackbar.service';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/store';
import { PageEnum, Predicates } from '../../types/enums';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { PageHeader } from "../../components/PageHeader";
import { LoadingDialog } from '../../components/HelperComponents/LoadingDialog';
import { IRequestedExpressions } from '../../types/expression';
import { DownloadUserDataExpressionBuilder } from '../../services/user-service';
import { DateTimePicker } from '@mui/x-date-pickers';
import { RedirectToDash } from '.././RedirectToDash';
import { Protoexpression } from '../../utilities/ProtoExpression';

/**
 * @returns - a page which allows for selecting dates and downloading data
 * @remarks The Download user data page allows for System Admin users to select a start
 * and end date range, and then download user data for that date range as a csv file.
 */
export function DownloadUserData() {
    const userRole = useSelector((state: RootState) => state.roles);
    const currentPage = userRole.userPageAccess[PageEnum.DownloadUserData];
    const classes = useStyles();
    const [startDateTime, setStartDateTime] = useState(new Date());
    const [endDateTime, setEndDateTime] = useState<Date>(new Date());
    const [isLoading, setIsLoading] = useState(false);
    const [isDisabled, setDisabled] = useState(true);
    const difference = dayjs(endDateTime).diff(dayjs(startDateTime), 'hours');

    const theme = createTheme({
        palette: {
            primary: {
                main: '#10AD61'
            },
            secondary: {
                main: '#10AD61'
            },
            background: {
                default: '#ffffff'
            },
        },
        typography: {
            h6: {
                color: '#484A57'
            },
            body1: {
                color: 'black'
            },
            subtitle1: {
                color: '#21C452',
                textDecoration: 'none',
                '&:visited': {
                    color: '#06bd9b',
                },
            }
        },
    });

    const handleStartDate = (date) => {
        if (date) {
            setStartDateTime(date.$d);
        }
    }

    const handleEndDate = (date) => {
        if (date) {
            setEndDateTime(date.$d);
        }
    }

    const getUserData = async (expression: IRequestedExpressions) => {
        try {
            const certBlob = await DownloadUserDataExpressionBuilder(expression);
            const blob = new Blob([certBlob], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            const url = URL.createObjectURL(blob);
            const anchor = document.createElement('a');
            anchor.href = url;
            const start: string = startDateTime.toDateString();
            const end: string = endDateTime.toDateString();
            anchor.download = `Submission Data From  ${start}  to ${end}.xlsx`;
            anchor.click();
            URL.revokeObjectURL(url);
        } catch (error) {
            throw error
        }
        finally {
            setIsLoading(false);
        }
    }

    const handleSubmit = (event) => {
        setIsLoading(true);
        if (difference <= 0) {
            showSnackbar("invalid Date Range", "warning", 5000)
            setIsLoading(false);
        }
        else {
            getUserData(
                {
                    protoexpressions: [
                        Protoexpression("SubmissionCreateDateTime", startDateTime.toDateString(), Predicates.GreaterThan),
                        Protoexpression("SubmissionCreateDateTime", endDateTime.toDateString(), Predicates.LessThanEquals)
                    ],
                    connectiveIds: [2]
                }
            )
        }
        event.preventDefault();
    }

    useEffect(() => {
        setDisabled(difference <= 0);
    }, [startDateTime, endDateTime]);

    return (
        <div>
            <ThemeProvider theme={theme}>
                {currentPage?.read && <Grid className={classes.formContainer} alignItems={"center"} container>
                    <Grid item xs={12}>
                        <Grid
                            container
                            rowSpacing={4}
                            className={classes.subWrapper2}
                            direction={"column"}
                            justifyContent={'center'}
                            alignItems={"center"}
                        >
                            <Grid item xs={12}>
                                <Grid container justifyContent={"center"} alignItems={"center"} direction={"column"}>
                                    <Grid item xs={6}>
                                        <PageHeader title={"Download User Data"} onBackClick={undefined} />
                                    </Grid>
                                    <Grid item xs={4} minHeight={'10vh'}>
                                        <Grid container justifyContent={"center"} alignItems={"center"}>
                                            <Grid item>
                                                {difference <= 0 &&
                                                    <Typography variant='button'>Please select a valid date range</Typography>}
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                </Grid>
                                <Grid container direction={"row"} columnSpacing={4} alignItems={"center"} justifyContent={"center"} className={classes.fixGridMargin}>
                                    <Grid item xs={12} >
                                        <Paper elevation={6}>
                                            <Grid
                                                container
                                                direction={"row"}
                                                justifyContent="center"
                                                alignItems="center"
                                                className={classes.gridPadding}
                                                columnSpacing={4}
                                            >
                                                <Grid item xs={6}>
                                                    <FormControl>
                                                        <Typography>Submit Start Date</Typography>
                                                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                                                            <DateTimePicker
                                                                defaultValue={dayjs(startDateTime)}
                                                                disableFuture
                                                                views={['year', 'month', 'day', 'hours']}
                                                                closeOnSelect
                                                                onChange={handleStartDate}
                                                            />
                                                        </LocalizationProvider>
                                                    </FormControl>
                                                </Grid>
                                                <Grid item xs={6}>
                                                    <FormControl>
                                                        <Typography>Submit End Date</Typography>
                                                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                                                            <DateTimePicker
                                                                defaultValue={dayjs(endDateTime)}
                                                                disableFuture
                                                                views={['year', 'month', 'day', 'hours']}
                                                                closeOnSelect
                                                                onChange={handleEndDate}
                                                            />
                                                        </LocalizationProvider>
                                                    </FormControl>
                                                </Grid>
                                            </Grid>
                                        </Paper>
                                    </Grid>
                                </Grid>
                                <Grid container justifyContent={'center'} alignItems={"center"} className={classes.gridPaddingTop} >
                                    <Grid item xs={5} alignSelf={"center"} className={classes.gridPaddingTop} >
                                        <Grid container direction={"column"} rowSpacing={3} justifyContent={'center'} alignItems={"center"}>
                                            <Grid item xs={4} >
                                                <Button onClick={handleSubmit} disabled={isDisabled} variant="contained" fullWidth>
                                                    Download
                                                </Button>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                        {isLoading &&
                            <LoadingDialog
                                loading={isLoading} />
                        }
                    </Grid>
                </Grid>
                }
                {!currentPage?.read && currentPage?.read !== undefined &&
                    <RedirectToDash />
                }
            </ThemeProvider>
        </div >
    );
}